import './_profile.style.scss';

import {
  faBuilding,
  faCalendar,
  faClock,
  faEnvelope,
  faLock,
  faTimes,
  faUser,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Badge, Button, Divider, Modal, Tag } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { selectUser } from '../redux/selectors';
import { UserActivity } from '../redux/types';
import ChangePasswordModal from './ChangePasswordModal';

type UserProfileModalProps = {
  onClose: () => void;
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const getInitials = (name: string, username: string): string => {
  const parts = (name || username || '').trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0] || 'U').slice(0, 2).toUpperCase();
};

type InfoRowProps = {
  icon: React.ComponentProps<typeof FontAwesomeIcon>['icon'];
  label: string;
  value: React.ReactNode;
  children?: React.ReactNode;
};

const InfoRow = ({ icon, label, value, children }: InfoRowProps) => (
  <div className="l-profile__info-row">
    <span className="l-profile__info-icon">
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="l-profile__info-content">
      <span className="l-profile__info-label">{label}</span>
      <span className="l-profile__info-value">{value}</span>
      {children}
    </div>
  </div>
);

const ACTIVITY_STATS: { key: keyof UserActivity; label: string }[] = [
  { key: 'variants_submitted', label: 'Variants submitted' },
  { key: 'assessments_performed', label: 'Assessments performed' },
  { key: 'assessments_confirmed', label: 'Assessments confirmed' },
  { key: 'comments_written', label: 'Comments written' },
];

const UserProfileModal = ({ onClose }: UserProfileModalProps) => {
  const profile = useSelector(selectUser);
  const [isChangePasswordVisible, setIsChangePasswordVisible] = useState(false);

  const activity = profile?.activity ?? null;
  const initials = profile ? getInitials(profile.name, profile.username) : '?';

  return (
    <>
      <Modal
        open
        closable={false}
        footer={null}
        title={null}
        maskClosable
        onCancel={onClose}
        centered
        width="auto"
        zIndex={3000}
      >
        <div className="l-profile__header">
          <div className="l-profile__header-title-row">
            <div className="l-profile__header-title">
              <FontAwesomeIcon icon={faUser} />
              My Profile
            </div>
            <div className="l-profile__header-close" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </div>
          </div>
        </div>

        <Divider className="l-profile__divider--top" />

        <div className="l-profile__avatar-section">
          <Avatar className="l-profile__avatar" size={64}>
            {initials}
          </Avatar>
          <div className="l-profile__avatar-info">
            <strong className="l-profile__name">{profile?.name || '—'}</strong>
            <span className="l-profile__username">
              @{profile?.username || '—'}
            </span>
            <div className="l-profile__status-badge">
              <Badge
                status={profile?.is_active ? 'success' : 'error'}
                text={profile?.is_active ? 'Active' : 'Inactive'}
              />
            </div>
            <Button
              className="l-profile__change-pw-btn"
              type="primary"
              size="small"
              icon={<FontAwesomeIcon icon={faLock} />}
              onClick={() => setIsChangePasswordVisible(true)}
            >
              Change Password
            </Button>
          </div>
        </div>

        <Divider className="l-profile__divider--section" />

        <div className="l-profile__info-section">
          <InfoRow
            icon={faEnvelope}
            label="Email"
            value={profile?.email || '—'}
          />

          {profile?.institution && (
            <InfoRow
              icon={faBuilding}
              label="Institution"
              value={profile.institution.name}
            >
              {profile.institution.address && (
                <span className="l-profile__institution-address">
                  {profile.institution.address}
                </span>
              )}
            </InfoRow>
          )}

          <InfoRow
            icon={faCalendar}
            label="Member since"
            value={profile?.date_joined ? formatDate(profile.date_joined) : '—'}
          />

          <InfoRow
            icon={faClock}
            label="Last login"
            value={profile?.last_login ? formatDate(profile.last_login) : '—'}
          />

          {profile?.roles && profile.roles.length > 0 && (
            <InfoRow
              icon={faUserShield}
              label="Roles"
              value={
                <div className="l-profile__roles-list">
                  {profile.roles.map((role) => (
                    <Tag key={role} color="blue">
                      {role}
                    </Tag>
                  ))}
                </div>
              }
            />
          )}
        </div>

        <Divider className="l-profile__divider--section" />

        <div className="l-profile__activity-section">
          <div className="l-profile__activity-title">Activity</div>
          <div className="l-profile__activity-grid">
            {ACTIVITY_STATS.map(({ key, label }) => (
              <div key={key} className="l-profile__activity-card">
                <span className="l-profile__activity-value">
                  {activity?.[key] ?? 0}
                </span>
                <span className="l-profile__activity-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {isChangePasswordVisible && (
        <ChangePasswordModal
          onClose={() => setIsChangePasswordVisible(false)}
        />
      )}
    </>
  );
};

export default UserProfileModal;
